import { PropsWithChildren, ReactNode } from 'react';
import { Radio } from '../../components/radio/radio';
import { Field, FieldProps } from '../../components/field/field';
import { Checkbox } from '../../components/checkbox/checkbox';
import { Typography } from '../../components/typography/typography';
import parse from 'html-react-parser';

type ConfigPropertyProps<T = unknown> = T &
  PropsWithChildren<{ name?: string; property: string; description?: string; helper?: ReactNode }>;

export const ConfigSection = ({ children }: { children?: ReactNode }) => {
  return <div className="flex flex-col gap-4 flex-nowrap">{children}</div>;
};

export const ConfigProperty = ({ property, helper, description, children }: ConfigPropertyProps) => {
  return (
    <div className="flex gap-2 items-start shrink-0">
      <div className="basis-1/4 flex gap-1 items-center">
        {property}
        {helper}
      </div>

      <div className="basis-3/4">
        <div className="flex flex-col gap-1">
          <div>{children}</div>

          {description && (
            <Typography type="text-xs" className="!text-grey-700 !italic">
              {parse(description)}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

export const ConfigBinaryProperty = ({
  name,
  value,
  onChange,
  ...props
}: ConfigPropertyProps<{ value: boolean; onChange?: (v: boolean) => void }>) => {
  return (
    <ConfigProperty {...props}>
      <div className="flex gap-2">
        <Radio
          name={name}
          label="No"
          isChecked={!value}
          value="0"
          onChange={(checked) => checked && onChange?.(false)}
        />
        <Radio
          name={name}
          label="Yes"
          isChecked={value}
          value="1"
          onChange={(checked) => checked && onChange?.(true)}
        />
      </div>
    </ConfigProperty>
  );
};

export const ConfigCheckboxProperty = ({
  name,
  description,
  value,
  onChange,
  ...props
}: ConfigPropertyProps<{ value: boolean; onChange?: (v: boolean) => void }>) => {
  return (
    <ConfigProperty {...props}>
      <Checkbox
        name={name}
        isChecked={value}
        onChange={(v) => onChange?.(v)}
        label={
          <Typography type="text-xs" className="!text-grey-700 !italic">
            {description}
          </Typography>
        }
      />
    </ConfigProperty>
  );
};

export const ConfigInputProperty = ({
  type,
  name,
  value,
  onChange,
  disabled = false,
  ...props
}: ConfigPropertyProps<{ type: FieldProps['type']; value: any; onChange?: (v: any) => void; disabled?: boolean }>) => {
  return (
    <ConfigProperty {...props}>
      <Field name={name} type={type} value={value} onChange={(v) => onChange?.(v)} disabled={disabled} />
    </ConfigProperty>
  );
};
